import { Component } from '@theme/component';

/**
 * A custom element that handles mobile sticky navigation.
 *
 * @extends {Component}
 */
class MobileStickyNav extends Component {
  /**
   * Checks if the current device is mobile.
   * @returns {boolean} True if mobile, false otherwise.
   */
  isMobileDevice() {
    // Check using media query (matches common mobile breakpoints)
    const mobileMediaQuery = window.matchMedia('(max-width: 1199px)');
    return mobileMediaQuery.matches;
  }

  /**
   * Checks if the sticky navigation element exists and is visible.
   * @returns {boolean} True if element exists and should be active, false otherwise.
   */
  shouldInitialize() {
    // Check if element exists
    if (!this || !this.isConnected) {
      return false;
    }

    // Check if we're on mobile
    if (!this.isMobileDevice()) {
      return false;
    }

    // Check if element is visible (not hidden by CSS)
    const styles = window.getComputedStyle(this);
    if (styles.display === 'none' || styles.visibility === 'hidden') {
      return false;
    }

    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    // Don't initialize on desktop or if element doesn't exist
    if (!this.shouldInitialize()) {
      return;
    }

    // Track previously active item for drawer close restoration
    this.previousActiveItem = null;

    // Initialize wishlist counter
    this.updateWishlistCounter();

    // Listen for wishlist updates (custom event)
    document.addEventListener('wishlist:update', this.onWishlistUpdate);
    
    // Also listen for storage events for cross-tab synchronization
    window.addEventListener('storage', this.onStorageChange);

    // Poll for wishlist changes (for same-tab updates)
    this.wishlistPollInterval = setInterval(() => {
      this.checkWishlistChange();
    }, 1000);

    // Check for wishlist count in localStorage on load
    this.syncWishlistFromStorage();

    // Listen for clicks on desktop wishlist to sync counter
    const desktopWishlist = document.querySelector('.wishlist-icon, [href*="wishlist"]');
    if (desktopWishlist) {
      desktopWishlist.addEventListener('click', () => {
        setTimeout(() => this.updateWishlistCounter(), 500);
      });
    }

    // Set up OWNDGRAM drawer button handlers
    const owndgramButtons = this.querySelectorAll('[data-open-owndgram]');
    owndgramButtons.forEach(button => {
      button.addEventListener('click', this.openOwndgramDrawer);
      button.addEventListener('click', this.toggleActiveState);
    });

    // Set up account button handlers
    const accountButtons = this.querySelectorAll('[data-open-account]');
    accountButtons.forEach(button => {
      button.addEventListener('click', this.openAccount);
      button.addEventListener('click', this.toggleActiveState);
    });

    // Set up link click handlers for active state and nav opening
    const navLinks = this.querySelectorAll('.mobile-sticky-nav__link[href]');
    if (navLinks && navLinks.length > 0) {
      navLinks.forEach(link => {
        // Check if this link should open the nav menu
        if (link.dataset.openNavOnClick === 'true') {
          link.addEventListener('click', this.handleNavMenuClick);
        } else {
          link.addEventListener('click', this.toggleActiveState);
        }
      });
    }

    // Listen for drawer close events
    this.setupDrawerCloseListener();

    // Listen for resize events to handle viewport changes (debounced)
    this.resizeTimeout = null;
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (!this.shouldInitialize() && this.isInitialized) {
          // If switched to desktop, clean up
          this.cleanup();
        } else if (this.shouldInitialize() && !this.isInitialized) {
          // If switched to mobile and not initialized, reinitialize
          this.connectedCallback();
        }
      }, 250);
    };
    window.addEventListener('resize', this.handleResize);

    // Set active state based on current page
    this.setActiveStateFromURL();

    // Mark as initialized
    this.isInitialized = true;
  }

  /**
   * Cleans up event listeners and intervals.
   */
  cleanup() {
    if (!this.isInitialized) {
      return;
    }

    document.removeEventListener('wishlist:update', this.onWishlistUpdate);
    window.removeEventListener('storage', this.onStorageChange);
    
    if (this.wishlistPollInterval) {
      clearInterval(this.wishlistPollInterval);
      this.wishlistPollInterval = null;
    }

    this.isInitialized = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up resize handler
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
    }

    // Clean up other listeners
    this.cleanup();
  }

  /**
   * Opens the OWNDGRAM drawer.
   */
  openOwndgramDrawer = (event) => {
    location.href = '/pages/owndgram';
  };

  /**
   * Closes the OWNDGRAM drawer.
   */
  closeOwndgramDrawer = () => {
    const drawer = this.querySelector('.owndgram-drawer__dialog');
    if (!drawer) return;

    // Clean up event listeners
    const closeButton = drawer.querySelector('[data-close-drawer]');
    if (closeButton && closeButton._closeHandler) {
      closeButton.removeEventListener('click', closeButton._closeHandler);
      delete closeButton._closeHandler;
    }

    if (drawer._backdropHandler) {
      drawer.removeEventListener('click', drawer._backdropHandler);
      delete drawer._backdropHandler;
    }

    drawer.close();
    const button = this.querySelector('[aria-controls="' + drawer.id + '"]');
    if (button) {
      button.setAttribute('aria-expanded', 'false');
    }
  };

  /**
   * Opens account actions (triggers click on desktop account button).
   */
  openAccount = () => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    // Find the account button in the header
    const accountButton = document.querySelector('header-actions account-button, header-actions [data-account-button]');
    if (accountButton) {
      accountButton.click();
    } else {
      // Fallback: redirect to account page or login
      if (window.Shopify?.customer) {
        window.location.href = '/account';
      } else {
        window.location.href = '/account/login';
      }
    }
  };

  /**
   * Updates the wishlist counter.
   */
  updateWishlistCounter = () => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    const badge = this.querySelector('[data-wishlist-count]');
    if (!badge) return;

    // Get wishlist count from localStorage
    const wishlistData = localStorage.getItem('wishlist-items');
    let count = 0;

    if (wishlistData) {
      try {
        const items = JSON.parse(wishlistData);
        count = Array.isArray(items) ? items.length : 0;
      } catch (e) {
        console.warn('Failed to parse wishlist data:', e);
      }
    }

    badge.textContent = count > 0 ? (count > 99 ? '99+' : String(count)) : '';
    
    // Store in sessionStorage for quick access
    sessionStorage.setItem('wishlist-count', String(count));
  };

  /**
   * Syncs wishlist counter from localStorage.
   */
  syncWishlistFromStorage = () => {
    this.updateWishlistCounter();
  };

  /**
   * Handles wishlist update events.
   */
  onWishlistUpdate = () => {
    this.updateWishlistCounter();
  };

  /**
   * Handles storage change events for cross-tab synchronization.
   */
  onStorageChange = (event) => {
    if (event.key === 'wishlist-items' || event.key === 'wishlist-count') {
      this.updateWishlistCounter();
    }
  };

  /**
   * Checks for wishlist changes by comparing localStorage.
   */
  checkWishlistChange = () => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    const currentData = localStorage.getItem('wishlist-items');
    if (this.lastWishlistData !== currentData) {
      this.lastWishlistData = currentData;
      this.updateWishlistCounter();
    }
  };

  /**
   * Sets active state based on current URL.
   */
  setActiveStateFromURL = () => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    const currentPath = window.location.pathname;
    const currentUrl = window.location.href;
    
    // Remove active state from all links
    const allLinks = this.querySelectorAll('.mobile-sticky-nav__link');
    if (allLinks && allLinks.length > 0) {
      allLinks.forEach(link => {
        link.classList.remove('is-active');
      });

      // Check each link and set active if it matches current URL
      let homepage = false;
      allLinks.forEach(link => {
        if (link.tagName === 'A' && link.href) {
          try {
            const linkUrl = new URL(link.href);
            const linkPath = linkUrl.pathname;
            
            // Check if link matches current page
            if (linkPath === currentPath || link.href === currentUrl) {
              link.classList.add('is-active');
            }
            // Special case for home page
            if (linkPath === '/' && currentPath === '/') {
              homepage = true;
            }
            if(currentPath.includes('/account')){
              document.querySelector('.mobile-sticky-nav__link[aria-label="Account"]')?.classList.add('is-active');
            }
          } catch (e) {
            // Skip invalid URLs
            console.warn('Invalid URL:', link.href);
          }
        }
      });
      const homeLink = document.querySelector('.mobile-sticky-nav__link[aria-label="Home"]');
      if (homepage) {
        if (homeLink) {
          homeLink.classList.add('is-active');
        }
      }else{
        if(homeLink) {  
          homeLink.querySelector('svg path').style.stroke = '#6d7280';
        }
        }
  }
  };

  /**
   * Sets up listener for drawer close events to restore previous active state.
   */
  setupDrawerCloseListener() {
    const drawerDetails = document.querySelector('#Details-menu-drawer-container');
    if (!drawerDetails) return;

    // Listen for toggle events on the details element
    drawerDetails.addEventListener('toggle', () => {
      // When drawer closes (details is no longer open)
      if (!drawerDetails.open && this.previousActiveItem) {
        // Remove active from current item
        const allLinks = this.querySelectorAll('.mobile-sticky-nav__link');
        allLinks.forEach(link => {
          link.classList.remove('is-active');
        });
        
        // Restore previous active item
        this.previousActiveItem.classList.add('is-active');
        this.previousActiveItem = null;
      }
    });
  }

  /**
   * Gets the currently active nav link.
   * @returns {HTMLElement|null} The currently active link element or null
   */
  getActiveNavLink() {
    const allLinks = this.querySelectorAll('.mobile-sticky-nav__link');
    for (const link of allLinks) {
      if (link.classList.contains('is-active')) {
        return link;
      }
    }
    return null;
  }

  /**
   * Removes active state from all nav links.
   */
  removeAllActiveStates() {
    const allLinks = this.querySelectorAll('.mobile-sticky-nav__link');
    allLinks.forEach(link => {
      link.classList.remove('is-active');
    });
  }

  /**
   * Handles clicks on nav items that should open the hamburger menu.
   */
  handleNavMenuClick = (event) => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const clickedElement = event.currentTarget || event.target.closest('.mobile-sticky-nav__link');
    if (!clickedElement) return;

    const drawerDetails = document.querySelector('#Details-menu-drawer-container');
    const wasDrawerOpen = drawerDetails && drawerDetails.open;

    // Store the previously active item before changing states
    // Only store if drawer is currently closed (we're about to open it)
    if (!wasDrawerOpen) {
      this.previousActiveItem = this.getActiveNavLink();
    }

    // Remove active from all links and set clicked one as active
    this.removeAllActiveStates();
    clickedElement.classList.add('is-active');

    // Only toggle drawer if it's closed (opening it)
    // If drawer is already open, don't toggle - just keep it open with new active item
    if (!wasDrawerOpen) {
      const headerDrawer = document.querySelector('header-drawer');
      if (drawerDetails && headerDrawer && typeof headerDrawer.open === 'function') {
        if (!drawerDetails.open) {
          drawerDetails.setAttribute('open', '');
        }
        headerDrawer.open();
      } else {
        document.querySelector('#Details-menu-drawer-container summary')?.click();
      }
    } else {
      const headerDrawer = document.querySelector('header-drawer');
      if (headerDrawer && typeof headerDrawer.close === 'function') {
        headerDrawer.close();
      } else {
        clickedElement.classList.remove('is-active');
        drawerDetails?.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        drawerDetails?.classList.remove('menu-open');
        setTimeout(() => {
          drawerDetails?.removeAttribute('open');
        }, 300);
      }
    }
  };

  /**
   * Toggles active state on clicked navigation items.
   */
  toggleActiveState = (event) => {
    // Don't execute on desktop
    if (!this.isMobileDevice()) return;
    
    const clickedElement = event.currentTarget || event.target.closest('.mobile-sticky-nav__link');
    if (!clickedElement) return;

    // Clear previous active item tracking (not needed for normal navigation)
    this.previousActiveItem = null;

    // Remove active state from all links
    this.removeAllActiveStates();

    // Add active state to clicked link
    clickedElement.classList.add('is-active');
  };
}

customElements.define('mobile-sticky-nav-component', MobileStickyNav);

